import { apiSlice } from '../api/apiSlice';
import { AuthRole } from '../../utils/roles';

export interface Profile {
  id: string;
  email: string;
  fullName: string | null;
  role: AuthRole | null;
  lastSignInAt?: string | null;
  createdAt?: string | null;
  employeeId?: string | null;
  employeeName?: string | null;
  employeeEmail?: string | null;
  departmentName?: string | null;
  teamName?: string | null;
  managerName?: string | null;
}

export interface SyncManagerLinksResult {
  employeesUpdated: number;
  departmentsUpdated: number;
  employeeManagerUpdates: number;
  employeeUserUpdates: number;
  departmentManagerUpdates: number;
  warnings: string[];
}

export interface ApprovalConfigHealth {
  salesVpEmails: string[];
  resolvedSalesVps: Array<{ id: string; name: string; email: string }>;
  unresolvedSalesVpEmails: string[];
  salesVpError: string | null;
  escalationEmails: string[];
  externalEscalationEmails: string[];
  escalationWarning: string | null;
  escalationError: string | null;
  backendPublicUrlConfigured: boolean;
  actionSecretConfigured: boolean;
  emailsDisabled: boolean;
}

export const adminApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProfiles: builder.query<Profile[], void>({
      query: () => '/admin/profiles',
      transformResponse: (response: { success: boolean; data: Profile[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Profile' as const, id })),
              { type: 'Profile', id: 'LIST' },
            ]
          : [{ type: 'Profile', id: 'LIST' }],
    }),
    getApprovalConfig: builder.query<ApprovalConfigHealth, void>({
      query: () => '/admin/approval-config',
      transformResponse: (response: { success: boolean; data: ApprovalConfigHealth }) => response.data,
    }),
    updateProfile: builder.mutation<Profile, { id: string; updates: Partial<Profile> }>({
      query: ({ id, updates }) => ({
        url: `/admin/profiles/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      transformResponse: (response: { success: boolean; data: Profile }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Profile', id },
        { type: 'Profile', id: 'LIST' },
        { type: 'Employee', id: 'LIST' },
        { type: 'Department', id: 'LIST' },
        { type: 'TimeOff', id: 'LIST' },
        { type: 'FloorplanRoster', id: 'LIST' },
      ],
    }),
    bulkUpdateProfiles: builder.mutation<Profile[], { updates: Array<{ id: string; role?: Profile['role']; fullName?: string | null }> }>({
      query: ({ updates }) => ({
        url: '/admin/profiles/bulk',
        method: 'POST',
        body: { updates },
      }),
      transformResponse: (response: { success: boolean; data: Profile[] }) => response.data,
      invalidatesTags: [
        { type: 'Profile', id: 'LIST' },
        { type: 'Employee', id: 'LIST' },
        { type: 'Department', id: 'LIST' },
        { type: 'TimeOff', id: 'LIST' },
        { type: 'FloorplanRoster', id: 'LIST' },
      ],
    }),
    createUser: builder.mutation<
      Profile,
      {
        email: string;
        fullName?: string | null;
        role?: Profile['role'];
        employeeId?: string | null;
        mode: 'invite' | 'password';
        temporaryPassword?: string;
      }
    >({
      query: (payload) => ({
        url: '/admin/users',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { success: boolean; data: Profile }) => response.data,
      invalidatesTags: [
        { type: 'Profile', id: 'LIST' },
        { type: 'Employee', id: 'LIST' },
        { type: 'Department', id: 'LIST' },
        { type: 'TimeOff', id: 'LIST' },
        { type: 'FloorplanRoster', id: 'LIST' },
      ],
    }),
    resetUserPassword: builder.mutation<void, { id: string; temporaryPassword: string }>({
      query: ({ id, temporaryPassword }) => ({
        url: `/admin/users/${id}/reset-password`,
        method: 'POST',
        body: { temporaryPassword },
      }),
    }),
    deleteUser: builder.mutation<{ id: string }, string>({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { success: boolean; data: { id: string } }) => response.data,
      invalidatesTags: (result, error, id) =>
        error
          ? []
          : [
              { type: 'Profile', id },
              { type: 'Profile', id: 'LIST' },
              { type: 'Employee', id: 'LIST' },
              { type: 'Department', id: 'LIST' },
              { type: 'TimeOff', id: 'LIST' },
              { type: 'FloorplanRoster', id: 'LIST' },
            ],
    }),
    syncManagerLinks: builder.mutation<SyncManagerLinksResult, void>({
      query: () => ({
        url: '/admin/sync-manager-links',
        method: 'POST',
      }),
      transformResponse: (response: { success: boolean; data: SyncManagerLinksResult }) => response.data,
      // Sync writes employees.manager_user_id/user_id AND departments.manager_user_id.
      // Everything that embeds those links must refresh: profiles surface
      // employeeId/employeeName from the user link, and time-off rows embed
      // employee.managerUserId that the approval pages filter on.
      invalidatesTags: [
        { type: 'Employee', id: 'LIST' },
        { type: 'Department', id: 'LIST' },
        { type: 'Profile', id: 'LIST' },
        { type: 'TimeOff', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetProfilesQuery,
  useGetApprovalConfigQuery,
  useUpdateProfileMutation,
  useBulkUpdateProfilesMutation,
  useCreateUserMutation,
  useResetUserPasswordMutation,
  useDeleteUserMutation,
  useSyncManagerLinksMutation,
} = adminApiSlice;
